import * as Types from './types';
import * as helpers from './helpers';
import { actionTypes, DATE_FORMAT, TIME_FORMAT, NUMBER_SEPARATOR, CURRENCY_SYMBOL_POSITION } from './constants';

const additionalAppDefaultState: Types.IAdditionalAppData = {
    areInitialResourcesLoaded: false,
    isAppInitExecuted: false,
    isQuickDepositAvailable: false,
    isReconnectVisible: false,
    isServerSideRendering: false,
    isChangeLangInProgress: false,
    isIntegrationScriptLoaded: null,
    isRegistrationTcVersionDisabled: false,
    oapiLoginTimeout: 10000
};

const initialResourcesMenuDefaultState: Types.IMenuData = {
    menuItems: []
};

const initialResourcesTagsDefaultState: Types.IAppTagsData = {
    tags: []
};

const oldGamesFeedsDefaultState: {
    isOldGamesFeedsLoaded: boolean;
    oldGamesFeeds: Types.IGameFeedConfig[];
} = {
    isOldGamesFeedsLoaded: false,
    oldGamesFeeds: []
};

const defaultState: Types.TAppState = {
    ...oldGamesFeedsDefaultState,
    ...initialResourcesMenuDefaultState,
    ...initialResourcesTagsDefaultState,
    ...additionalAppDefaultState,
    aboveHeaderWcIdDesktop: '',
    aboveHeaderWcIdMobile: '',
    aboveHeaderWcIdTablet: '',
    accountPage: '',
    allowedCountries: [],
    allowedCurrencies: [],
    allowedLocales: [],
    allowedRedirectHostNames: '',
    applicationWCID: '',
    balancePage: '',
    betLossLimitActions: {
        betlimitActions: '',
        losslimitActions: ''
    },
    biPlayerSegmentField: '',
    bingoSystemId: '',
    bonusManagementTCPreferences: {},
    cashierUrlTemplates: [],
    casino: '',
    casinoName: '',
    cdnUrl: '',
    chatCssUrl: '',
    chatJsUrl: '',
    chatWidget: {
        enabled: false,
        showIcon: false,
        counterPage: '',
        showLaunchIcon: false
    },
    chatWidgetConfiguration: '',
    chatWidgetSource: '',
    clientSkin: '',
    clientType: '',
    clientVersion: '',
    codeInsertion: {
        onAppInit: '',
        onPageRendered: '',
        onPageRendering: ''
    },
    companyLogo: '',
    cookiesConfiguration: {},
    currencySymbolPosition: CURRENCY_SYMBOL_POSITION,
    customFieldIndex: '',
    dataMapperEndpointURL: '',
    dataVerificationStatusFormName: '',
    dateFormat: DATE_FORMAT,
    defaultBingoRegion: '',
    defaultCountry: '',
    defaultCurrency: '',
    defaultLocale: '',
    depositPage: '',
    depositReminderLimits: [],
    desktopBingoPIGamesBaseURL: '',
    desktopCasinoClientBaseURL: '',
    detailsRecovery: {
        useEmailOnly: false,
        isRequiredVerificationEnable: false,
        enableSmsNotifications: false
    },
    dynamicCurrencyTags: {},
    dynamicTags: {},
    emailVerificationParam: '',
    enableAgeVerification: false,
    enableDataVerificationStatusCheck: false,
    enableGamblingSessionLimitPopup: false,
    enableGoogleAnalyticsOAPIEvent: false,
    enableSplashPage: false,
    endpointURL: '',
    footerWCID: '',
    galaxyClientType: '',
    galaxyClientVersion: '',
    gamesGridGameInfoPageURL: '',
    gamesGridViewMorePageURL: '',
    groupId: 1,
    headerWCID: '',
    http404Configuration: {},
    inboxWidget: {
        enabled: false,
        showIcon: false,
        counterPage: '',
        showLaunchIcon: false
    },
    isAdminNode: false,
    isAutoLoginEnabled: false,
    isEnablePasswordSpy: false,
    isLoginAgainPopup: false,
    isPostcodeEnabled: false,
    isQuickDepositEnabled: false,
    isShowSidebar: false,
    isItalianHeaderEnabled: false,
    isUsernameInUppercase: false,
    jackpotsCasino: '',
    languagePage: '',
    leaderboard: {},
    liveEndpointUrl: '',
    liveGuestVipLevel: '',
    localeSeparatorCurrencyMapping: [],
    locationJson: {},
    loginPage: '',
    loginWebContent: '',
    loginChangePassword: '',
    lottoRootURLLocaleMapping: {},
    lowBalanceAlertEnabled: false,
    lowBalanceAlertWcId: '',
    lowBalanceLimits: [],
    maintenance: {
        endDate: '',
        startDate: '',
        webContentId: '',
        isSchedulerEnabled: false,
        isAutoDetectEnabled: false,
        isMaintenanceEnabled: false
    },
    menuWCID: '',
    modifiedDate: 0,
    netVerifyJumioEnabled: false,
    numberSeparator: NUMBER_SEPARATOR,
    openApiDomain: '',
    pageInPopupUrls: [],
    paypalMerchantValue: '',
    pgg2: {
        casino: '',
        endpointUrl: '',
        guestsTracking: false
    },
    postcodeConfiguration: {
        url: '',
        key: '',
        userName: ''
    },
    postcodeProvider: '',
    promotionDefaultTerms: '',
    promotionInfoPageURL: '',
    quickDepositPage: '',
    quickDepositShowSuggestions: false,
    realMode: 1,
    redirectionRules: {},
    redirectionURLTemplates: [],
    redirects: {},
    registrationURL: '',
    requestStamp: window.Playtech?.Variables?.modifiedDate || Date.now(),
    rootPage: '',
    sendCashierMessagesToGS: false,
    seo: { useTrailingSlash: false },
    siteLogoConfigurations: {
        desktop: { siteLogoHeight: '', siteLogoWidth: '' },
        mobile: { siteLogoHeight: '', siteLogoWidth: '' },
        tablet: { siteLogoHeight: '', siteLogoWidth: '' }
    },
    skipAutoLoginDependency: true,
    useDirectPASRequests: false,
    sportsbookRootURLLocaleMapping: {},
    sessionSubscriptionBalances: '',
    sessionSubscriptionTags: '',
    sessionSubscriptionTypes: '',
    sessionWrapperSubscriptionTypes: '',
    showChatWidgetHeaderIcon: false,
    showSessionTicker: false,
    splashPage: '',
    ssoPasDomain: '',
    ssoPasExternalJsUrl: '',
    ssoPasJsURLs: '',
    themeName: '',
    timeFlyingPeriod: '',
    timeFormat: TIME_FORMAT,
    timeIsFlyingWebContent: '',
    translations: {},
    useIMSClientApi: false,
    useServicesCasinoJackpots: false,
    useServicesVfJackpots: false,
    userSegmentURLType: '',
    usernameEmailSwitcher: false,
    wcIdForNotSuspendedPlayer: '',
    wcIdForSuspendedPlayer: '',
    logoutOnDisconnect: true
};

/**
 * todo:
 * Implement immer reducer after legacy functionality in app/modules/configuration/configuration.js is migrated to app-react or removed.
 * Legacy module mutates Playtech.Variables with causes exception due to immer state immutability.
 */
export const reducer = (state = defaultState, action: Types.TAppAction): Types.TAppState => {
    switch (action.type) {
        case actionTypes.APP_FETCH: {
            const {
                app: {
                    dateFormat,
                    timeFormat,
                    clientSkin,
                    clientType,
                    dynamicTags,
                    ssoPasDomain,
                    ssoPasJsURLs,
                    openApiDomain,
                    clientVersion,
                    numberSeparator,
                    betLossLimitActions,
                    ssoPasExternalJsUrl,
                    currencySymbolPosition
                },
                areInitialResourcesLoaded
            } = action.payload;

            return {
                ...state,
                ...action.payload.app,
                ...areInitialResourcesLoaded && { areInitialResourcesLoaded },
                dateFormat: dateFormat || state.dateFormat,
                timeFormat: timeFormat || state.timeFormat,
                dynamicTags: helpers.parseDynamicTags(dynamicTags),
                numberSeparator: numberSeparator || state.numberSeparator,
                betLossLimitActions: betLossLimitActions || state.betLossLimitActions,
                currencySymbolPosition: currencySymbolPosition || state.currencySymbolPosition,
                ssoPasDomain: helpers.parseSuperDomainEndpoint(ssoPasDomain) || state.ssoPasDomain,
                ssoPasJsURLs: helpers.parseSuperDomainEndpoint(ssoPasJsURLs) || state.ssoPasJsURLs,
                openApiDomain: helpers.parseSuperDomainEndpoint(openApiDomain) || state.openApiDomain,
                clientType: helpers.getOverridableQueryStringParameter('clientType', clientType) || state.clientType,
                ssoPasExternalJsUrl: helpers.parseSuperDomainEndpoint(ssoPasExternalJsUrl) || state.ssoPasExternalJsUrl,
                clientVersion: helpers.getOverridableQueryStringParameter('clientVersion', clientVersion) || state.clientVersion,
                clientSkin: helpers.getOverridableQueryStringParameter('clientSkin', clientSkin) || state.clientSkin,
                isServerSideRendering: helpers.isServerSideRendering()
            };
        }

        case actionTypes.SET_RG_CONFIG: {
            const { rgConfig } = action.payload;

            return {
                ...state,
                rgConfig
            };
        }

        case actionTypes.UPDATE_HEADER_HEIGHT: {
            const { height, decrement } = action.payload;
            const { headerHeight = 0 } = state;

            return {
                ...state,
                headerHeight: decrement ? headerHeight - height : headerHeight + height
            };
        }

        case actionTypes.SET_APP_ERROR: {
            const { errorText } = action.payload;

            return {
                ...state,
                errorText,
                isErrorVisible: true
            };
        }

        case actionTypes.SET_IS_QUICK_DEPOSIT_AVAILABLE: {
            return {
                ...state,
                ...action.payload
            };
        }

        case actionTypes.SET_IGNORE_LOGOUT_NOTIFICATION: {
            return {
                ...state,
                ignoreLogoutNotification: action.payload
            };
        }

        case actionTypes.ADD_TAGS: {
            return {
                ...state,
                tags: action.payload
            };
        }

        case actionTypes.TOGGLE_IS_APP_INIT_EXECUTED: {
            return {
                ...state,
                isAppInitExecuted: action.payload
            };
        }

        case actionTypes.TOGGLE_RECONNECT: {
            return {
                ...state,
                isReconnectVisible: action.payload
            };
        }

        case actionTypes.UPDATE_MENU_ITEMS: {
            return {
                ...state,
                menuItems: action.payload
            };
        }

        case actionTypes.UPDATE_OLD_GAMES_FEEDS: {
            return {
                ...state,
                oldGamesFeeds: action.payload,
                isOldGamesFeedsLoaded: true
            };
        }

        case actionTypes.UPDATE_SPORTSBOOK_ROOT_URL: {
            return {
                ...state,
                ...action.payload,
                isSportsbookRootURLReady: true
            };
        }

        case actionTypes.UPDATE_LOTTO_ROOT_URL: {
            return {
                ...state,
                ...action.payload,
                isLottoRootURLReady: true
            };
        }

        case actionTypes.TOGGLE_IS_CHANGE_LANG_IN_PROGRESS: {
            return {
                ...state,
                isChangeLangInProgress: action.payload
            };
        }

        case actionTypes.SET_IS_INTEGRATION_SCRIPT_LOADED: {
            return {
                ...state,
                isIntegrationScriptLoaded: action.payload
            };
        }

        default:
            return state;
    }
};
